// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientProfileSelector } from './use-client-profile-selector';
import { AppIcon } from '@shared/ui-kit/components/app-icon';
import {
  Avatar,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  Tooltip,
  Typography,
} from '@mui/material';
import { IconButton } from '@shared/ui-kit';

/** The properties for configuring the client profile selector. */
export interface ClientProfileSelectorProps {
  /**
   * Indicates whether the component is expanded or collapsed, affecting the tooltip
   * placement.
   *
   * @defaultValue false
   */
  readonly expanded?: boolean;
}

/**
 * Renders a client profile selector component that allows users to select and
 * manage client profiles.
 */
export const ClientProfileSelector: FC<ClientProfileSelectorProps> = ({
  expanded,
}) => {
  const { t } = useTranslation('common');
  const data = useClientProfileSelector();

  const getTooltipTitle = () => {
    if (expanded) {
      return t('common.change-active-profile');
    }

    return data.selectedClientProfile?.name
      ? t('common.select-client-profile', { name: data.selectedClientProfile.name })
      : t('common.select-client-profile-none');
  };

  return (
    <>
      <Tooltip
        title={getTooltipTitle()}
        placement={expanded ? 'top' : 'right'}
        data-testid="client-profile-selector-tooltip"
        arrow
      >
        <button
          className="group flex w-full items-center gap-x-4 data-[open=true]:text-white"
          aria-expanded={expanded}
          data-open={data.open}
          aria-describedby={data.id}
          onClick={data.onClick}
          data-testid="client-profile-selector-button"
        >
          <Avatar
            className="!size-[20px] !bg-secondary !text-xs !font-normal"
            data-testid="client-profile-selector-avatar"
          >
            {data.selectedClientProfile?.avatar}
          </Avatar>
          <div className="hidden flex-col items-start group-aria-expanded:flex">
            <span className="max-w-[140px] truncate text-base font-medium">
              {data.selectedClientProfile?.name ??
                t('common.select-client-profile-none')}
            </span>
            {data.selectedClientProfile?.email && (
              <span className="max-w-[140px] truncate text-xs text-primary-medium">
                {data.selectedClientProfile.email}
              </span>
            )}
          </div>
          <AppIcon
            icon="ArrowDown"
            size={18}
            className="ml-auto hidden group-aria-expanded:block"
            color="disabled"
          />
        </button>
      </Tooltip>
      <Popover
        id={data.id}
        open={data.open}
        anchorEl={data.anchorEl}
        onClose={data.onClose}
        anchorOrigin={{
          vertical: expanded ? 'bottom' : 'top',
          horizontal: expanded ? 'left' : 'right',
        }}
        transformOrigin={{
          vertical: expanded ? 'top' : 'top',
          horizontal: 'left',
        }}
        classes={{
          paper:
            '!w-[250px] !bg-sidebar-popover !border-sidebar-border-active !border !border-px !text-white aria-[orientation=vertical]:!ml-[20px]',
        }}
        slotProps={{
          paper: {
            'aria-orientation': expanded ? 'horizontal' : 'vertical',
          },
        }}
      >
        {data.profiles.length === 0 && !data.isFetchingProfiles && (
          <Typography
            variant="body2"
            className="!w-full !justify-center !p-2"
            data-testid="no-client-profiles"
          >
            {t('common.no-profiles-found')}
          </Typography>
        )}
        {data.profiles.length > 0 && (
          <>
            <List
              sx={{ pt: 0, pb: 0, maxHeight: 240, overflowY: 'auto' }}
              dense
              data-testid="client-profile-list"
            >
              <ListItem sx={{ py: 0.5, px: 2 }}>
                <span className="text-sm text-primary-medium">
                  {t('common.client-profiles')}
                </span>
              </ListItem>

              {data.profiles.map(profile => (
                <ListItem key={profile.value} disablePadding>
                  <ListItemButton
                    onClick={() => data.onSelectProfile(profile.client)}
                    selected={data.selectedClientProfile?.id === profile.value}
                    sx={{
                      '&.Mui-selected': {
                        backgroundColor: 'rgba(66, 82, 150, 0.6)',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(66, 82, 150, 0.4)',
                      },
                    }}
                    data-testid={`client-profile-item-${profile.value}-button`}
                  >
                    <ListItemText
                      primary={profile.label}
                      secondary={profile.email}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{
                        variant: 'caption',
                        sx: { color: 'rgba(255, 255, 255, 0.5)' },
                      }}
                      data-testid={`client-profile-item-${profile.value}-label`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
            {data.pickerPageCount > 1 && (
              <div
                className="flex items-center justify-between border-t border-sidebar-border-active px-2 py-1"
                onMouseDown={event => event.stopPropagation()}
              >
                <IconButton
                  icon="ArrowLeft"
                  variant="icon"
                  size={16}
                  color="light"
                  hoverable={false}
                  onClick={data.onPreviousPage}
                  disabled={data.pickerPage <= 1}
                  aria-label={t('common.previous')}
                />
                <span className="text-xs text-primary-medium">
                  {data.pickerPage} / {data.pickerPageCount}
                </span>
                <IconButton
                  icon="ArrowRight"
                  variant="icon"
                  size={16}
                  color="light"
                  hoverable={false}
                  onClick={data.onNextPage}
                  disabled={data.pickerPage >= data.pickerPageCount}
                  aria-label={t('common.next')}
                />
              </div>
            )}
          </>
        )}
      </Popover>
    </>
  );
};
